import { ReactNode, useState } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import cn from 'classnames';

import { Icon } from '../Icon';

import styles from './TagsInput.module.scss';

export type TagsInputOption = {
    id: number;
    label: string;
    icon?: ReactNode;
};

export type TagsInputProps<T extends TagsInputOption> = {
    label: string;
    options: T[];
    value: T[];
    roValue?: T[];
    onChange: (items: T[]) => void;
    isLoading?: boolean;
    disabled?: boolean;
    placeholder?: string;
    noOptionsText?: string;
    typeToSearchThreshold?: number;
    minSearchLength?: number;
    searchValue?: string;
    onSearchChange?: (searchValue: string) => void;
};

type ChipProps = {
    label: string;
    onDelete?: () => void;
    icon?: ReactNode;
    readOnly?: boolean;
};

const slotProps = {
    paper: { className: styles.paper },
};

const Chip = ({ label, onDelete, icon, readOnly }: ChipProps) => (
    <span className={cn(styles.chip, readOnly && styles.chipReadOnly)}>
        {icon && <span className={styles.chipIcon}>{icon}</span>}
        <span className={styles.chipLabel}>{label}</span>
        {onDelete && (
            <button
                type="button"
                className={styles.chipRemoveButton}
                aria-label={`Remove ${label}`}
                onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onDelete();
                }}
            >
                <Icon name="close" />
            </button>
        )}
    </span>
);

export const TagsInput = <T extends TagsInputOption>({
    label,
    options,
    value,
    roValue = [],
    onChange,
    isLoading = false,
    disabled = false,
    placeholder = 'Type to search',
    noOptionsText = 'No options',
    typeToSearchThreshold = 25,
    minSearchLength = 2,
    searchValue: controlledSearchValue,
    onSearchChange,
}: TagsInputProps<T>) => {
    const [internalSearchValue, setInternalSearchValue] = useState('');
    const searchValue = controlledSearchValue ?? internalSearchValue;

    const setSearchValue = (nextValue: string) => {
        setInternalSearchValue(nextValue);
        onSearchChange?.(nextValue);
    };

    const shouldRequireTyping =
        options.length >= typeToSearchThreshold &&
        searchValue.length < minSearchLength;

    return (
        <div className={styles.wrapper}>
            <label className={styles.label}>{label}</label>
            <Autocomplete<T, true, false, false>
                multiple
                disabled={disabled}
                loading={isLoading}
                slotProps={slotProps}
                options={shouldRequireTyping ? [] : options}
                value={value}
                inputValue={searchValue}
                filterSelectedOptions
                noOptionsText={
                    shouldRequireTyping ? 'Type to search' : noOptionsText
                }
                isOptionEqualToValue={(option, selectedItem) =>
                    option.id === selectedItem.id
                }
                getOptionLabel={(option) => option.label}
                onInputChange={(_event, nextSearchValue) =>
                    setSearchValue(nextSearchValue)
                }
                onChange={(_event, nextItems) => onChange(nextItems)}
                renderOption={(props, option) => {
                    const { key, ...otherProps } = props;

                    return (
                        <li {...otherProps} key={key}>
                            <span className={styles.option}>
                                {option.icon && (
                                    <span className={styles.optionIcon}>
                                        {option.icon}
                                    </span>
                                )}
                                <span className={styles.optionLabel}>
                                    {option.label}
                                </span>
                            </span>
                        </li>
                    );
                }}
                renderValue={(selectedItems) =>
                    selectedItems.map((item) => (
                        <Chip
                            key={item.id}
                            label={item.label}
                            icon={item.icon}
                            onDelete={() => {
                                onChange(
                                    value.filter(
                                        (selectedItem) =>
                                            selectedItem.id !== item.id,
                                    ),
                                );
                            }}
                        />
                    ))
                }
                renderInput={(params) => (
                    <TextField
                        {...params}
                        className={styles.field}
                        placeholder={
                            value.length > 0 || roValue.length > 0
                                ? ''
                                : placeholder
                        }
                        slotProps={{
                            ...params.slotProps,
                            input: {
                                ...params.slotProps?.input,
                                startAdornment: (
                                    <>
                                        {
                                            params.slotProps?.input
                                                ?.startAdornment
                                        }
                                        {roValue.map((item) => (
                                            <Chip
                                                key={`read-only-${item.id}`}
                                                label={item.label}
                                                icon={item.icon}
                                                readOnly
                                            />
                                        ))}
                                    </>
                                ),
                            },
                        }}
                    />
                )}
            />
        </div>
    );
};
